import React, { useId } from 'react';
import { Button, Dropdown, Row, Col } from 'react-bootstrap';
import {
	BsArrowCounterclockwise,
	BsArrowRepeat,
	BsSymmetryVertical,
	BsZoomIn,
} from 'react-icons/bs';
import BayRangeSlider from '../../BayRangeSlider';
import useMediaQuery from '../../utilities/hook/useMediaQuery';
import styles from '../BayImageCropModal.module.scss';
import type { AspectPreset, ControlMode, SliderLayout } from '../index';

interface SplitLayoutProps {
	cropperContent: React.ReactNode;
	splitContent?: React.ReactNode;
	t: (_key: string) => string;
	/**
	 * Overrides the outer Row's className entirely (replaces the default
	 * `g-0 bay-image-crop-split-layout flex-fill h-100`, does not merge).
	 * Needed because `h-100` is a Bootstrap utility class and ships its own
	 * `!important`, so a merged-in class can never win a height fight against
	 * it -- only replacing the class list can.
	 */
	splitLayoutClassName?: string;
	/** Merged onto the outer Row's default inline style (`{ minHeight: 0 }`). */
	splitLayoutStyle?: React.CSSProperties;
	/** Merged onto the cropper column's default inline style (`{ minHeight: 0 }`). */
	splitMainStyle?: React.CSSProperties;
}

interface ToolbarControlsProps {
	t: (_key: string) => string;
	isProcessing: boolean;
	showCropShapeControl: boolean;
	activePreset: AspectPreset;
	presetLabels: Record<AspectPreset, string>;
	presetIcons: Record<
		AspectPreset,
		React.ComponentType<{ size?: number; className?: string }>
	>;
	onPresetChange: (_preset: AspectPreset) => void;
	onRotateLeft: () => void;
	onFlipHorizontal: () => void;
	onReset: () => void;
}

interface AdjustmentControlsProps {
	t: (_key: string) => string;
	isProcessing: boolean;
	sliderLayout: SliderLayout;
	controlMode: ControlMode;
	onControlModeChange: (_mode: ControlMode) => void;
	rotationDegrees: number;
	onRotationChange: (_value: number) => void;
	showRotationTickMarks: boolean;
	minZoom: number;
	maxZoom: number;
	zoom: number;
	zoomDisplayOffsetPercent?: number;
	onZoomChange: (_value: number) => void;
	showScaleTickMarks: boolean;
	showScaleRangeLabels: boolean;
	isSplitRotationView?: boolean;
}

export const ToolbarControls: React.FC<ToolbarControlsProps> = ({
	t,
	isProcessing,
	showCropShapeControl,
	activePreset,
	presetLabels,
	presetIcons,
	onPresetChange,
	onRotateLeft,
	onFlipHorizontal,
	onReset,
}) => {
	const ActivePresetIcon = presetIcons[activePreset];

	return (
		<div
			className="d-flex gap-2 flex-wrap align-items-center flex-shrink-0 pt-3 pb-1 mb-0"
		>
			<Button
				variant="light"
				onClick={onRotateLeft}
				disabled={isProcessing}
				className="px-3 py-2 rounded-1 border text-secondary"
			>
				<BsArrowCounterclockwise className="me-1" size={14} />
				{t('Rotate Left')}
			</Button>
			<Button
				variant="light"
				onClick={onFlipHorizontal}
				disabled={isProcessing}
				className="px-3 py-2 rounded-1 border text-secondary"
			>
				<BsSymmetryVertical className="me-1" size={14} />
				{t('Flip Horizontal')}
			</Button>

			{showCropShapeControl && (
				<Dropdown className={styles.cropShapeDropdown}>
					<Dropdown.Toggle
						variant="light"
						size="sm"
						disabled={isProcessing}
						className="py-1 px-3 border text-secondary"
					>
						<ActivePresetIcon className="me-1" size={14} />
						{t('Crop shape')}: {t(presetLabels[activePreset])}
					</Dropdown.Toggle>
					<Dropdown.Menu>
						{(Object.keys(presetLabels) as AspectPreset[]).map((preset) => (
							<Dropdown.Item
								key={preset}
								active={activePreset === preset}
								onClick={() => onPresetChange(preset)}
							>
								{React.createElement(presetIcons[preset], {
									className: `${styles.cropShapeIcon} me-2`,
									size: 14,
								})}
								{t(presetLabels[preset])}
							</Dropdown.Item>
						))}
					</Dropdown.Menu>
				</Dropdown>
			)}

			<Button
				variant="link"
				onClick={onReset}
				onMouseUp={(e) => {
					e.currentTarget.blur();
				}}
				disabled={isProcessing}
				className="ms-auto d-flex align-items-center gap-1"
			>
				<BsArrowRepeat size={14} />
				<span className="fw-medium">{t('Reset')}</span>
			</Button>
		</div>
	);
};

export const AdjustmentControls: React.FC<AdjustmentControlsProps> = ({
	t,
	isProcessing,
	sliderLayout,
	controlMode,
	onControlModeChange,
	rotationDegrees,
	onRotationChange,
	showRotationTickMarks,
	minZoom,
	maxZoom,
	zoom,
	zoomDisplayOffsetPercent = 0,
	onZoomChange,
	showScaleTickMarks,
	showScaleRangeLabels,
	isSplitRotationView = false,
}) => {
	const rotationSliderTickListId = useId();
	const scaleSliderTickListId = useId();

	const getSliderFillPercent = (value: number, min: number, max: number) => {
		if (max <= min) {
			return 0;
		}

		return ((value - min) / (max - min)) * 100;
	};

	const getSliderStyle = (value: number, min: number, max: number) => {
		const fillPercent = Math.max(0, Math.min(100, getSliderFillPercent(value, min, max)));
		return {
			'--bay-slider-fill-percent': `${fillPercent}%`,
		} as React.CSSProperties;
	};

	const rotationSliderValue = rotationDegrees < 0 ? rotationDegrees + 360 : rotationDegrees;

	const toDisplayZoomPercent = (value: number) => Math.round((value * 100) + zoomDisplayOffsetPercent);

	const renderRotationSlider = () => (
		<div className={`${styles.sliderPanel} ${isSplitRotationView ? 'flex-1 border rounded py-3 px-4' : ''}`}>
			<div className="d-flex align-items-center gap-2">
				<BsArrowCounterclockwise size={14} className="text-muted flex-shrink-0" />
				<div className={styles.sliderTrackWrap}>
					<BayRangeSlider
						min={0}
						max={360}
						step={1}
						value={rotationSliderValue}
						onChange={(e) => onRotationChange(parseInt(e.target.value, 10))}
						disabled={isProcessing}
						className="w-100 p-0 bg-transparent"
						style={getSliderStyle(rotationSliderValue, 0, 360)}
						{...(showRotationTickMarks ? { list: rotationSliderTickListId } : {})}
					/>
					{showRotationTickMarks ? (
						<div className={styles.tickLabels}>
							<span>0°</span>
							<span>180°</span>
							<span>360°</span>
						</div>
					) : null}
				</div>
				<div className="vr h-50 align-self-center" />
				<div className="d-flex align-items-center gap-2 flex-shrink-0" style={{ minWidth: '44px' }}>

					<span
						className="text-body fw-semibold lh-1 text-start"
						style={{ minWidth: '40px', fontSize: '1.08rem', letterSpacing: '0.01em' }}
					>
						{rotationSliderValue}°
					</span>
				</div>
			</div>
			{showRotationTickMarks ? (
				<datalist id={rotationSliderTickListId}>
					<option value={0} label="0°" />
					<option value={60} />
					<option value={90} />
					<option value={120} />
					<option value={180} label="180°" />
					<option value={240} />
					<option value={270} />
					<option value={300} />
					<option value={360} label="360°" />
				</datalist>
			) : null}
		</div>
	);

	const renderScaleSlider = () => (
		<div className={`${styles.sliderPanel} ${isSplitRotationView ? 'flex-1 border rounded py-3 px-4' : ''}`}>
			<div className="d-flex align-items-center gap-2">
				<BsZoomIn size={15} className="text-muted flex-shrink-0" />
				<div className="flex-fill d-flex flex-column" style={{ minWidth: 0 }}>
					<BayRangeSlider
						min={minZoom}
						max={maxZoom}
						step={0.1}
						value={zoom}
						onChange={(e) => onZoomChange(parseFloat(e.target.value))}
						disabled={isProcessing}
						aria-label="Scale slider"
						className="w-100 p-0 bg-transparent"
						style={getSliderStyle(zoom, minZoom, maxZoom)}
						{...(showScaleTickMarks ? { list: scaleSliderTickListId } : {})}
					/>
					{showScaleRangeLabels ? (
						<div className="d-flex justify-content-between text-muted fs-7 p-1 pe-0">
							<span>{toDisplayZoomPercent(minZoom)}%</span>
							<span>{toDisplayZoomPercent((minZoom + maxZoom) / 2)}%</span>
							<span>{toDisplayZoomPercent(maxZoom)}%</span>
						</div>
					) : null}
				</div>
				<div className="vr h-50 align-self-center" />
				<div className="d-flex align-items-center gap-2 flex-shrink-0" style={{ minWidth: '44px' }}>

					<span
						className="text-body fw-semibold lh-1 text-end"
						style={{ minWidth: '40px', fontSize: '1.08rem', letterSpacing: '0.01em' }}
					>
						{toDisplayZoomPercent(zoom)}%
					</span>
				</div>
			</div>
			{showScaleTickMarks ? (
				<datalist id={scaleSliderTickListId}>
					<option value={minZoom} />
					<option
						value={Math.min(maxZoom, minZoom + (maxZoom - minZoom) * 0.25)}
					/>
					<option
						value={Math.min(maxZoom, minZoom + (maxZoom - minZoom) * 0.5)}
					/>
					<option
						value={Math.min(maxZoom, minZoom + (maxZoom - minZoom) * 0.75)}
					/>
					<option value={maxZoom} />
				</datalist>
			) : null}
		</div>
	);

	return (
		<div className={`rounded-2 bg-light-subtle mb-0 flex-shrink-0 mt-1 ${isSplitRotationView ? '' : 'border py-3 px-4'}`}>
			{sliderLayout === 'toggle' ? (
				<>
					<div className="d-inline-flex gap-2 p-1 border rounded-pill overflow-hidden mx-auto mb-3 gap-1">
						<Button
							variant={
								controlMode === 'rotation' ? 'secondary' : 'outline-secondary'
							}
							size="sm"
							onClick={() => onControlModeChange('rotation')}
							disabled={isProcessing}
							className="rounded-pill"
							style={{ minWidth: '90px' }}
						>
							{t('Rotation')}
						</Button>
						<Button
							variant={
								controlMode === 'scale' ? 'secondary' : 'outline-secondary'
							}
							size="sm"
							onClick={() => onControlModeChange('scale')}
							disabled={isProcessing}
							className="rounded-pill"
							style={{ minWidth: '90px' }}
						>
							{t('Scale')}
						</Button>
					</div>
					{controlMode === 'rotation' ? renderRotationSlider() : renderScaleSlider()}
				</>
			) : (
				<div className={`d-flex gap-3 ${isSplitRotationView ? 'flex-row-reverse' : 'flex-column'}`}>
					{renderScaleSlider()}
					{renderRotationSlider()}
				</div>
			)}
		</div>
	);
};

/**
 * Split Layout - Two column layout with cropper on left, custom content on right
 * Use case: Image configuration with preview, metadata editing
 */
export const SplitLayout: React.FC<SplitLayoutProps> = ({
	cropperContent,
	splitContent,
	t,
	splitLayoutClassName,
	splitLayoutStyle,
	splitMainStyle,
}) => {
	const isMobile = useMediaQuery('(max-width: 991.98px)');

	return (
		<Row
			className={splitLayoutClassName || 'g-0 bay-image-crop-split-layout flex-fill h-100'}
			style={{ minHeight: 0, ...splitLayoutStyle }}
		>
			<Col
				xs={12}
				lg={6}
				className={`px-4 py-3 bay-image-crop-split-main d-flex flex-column ${isMobile ? 'border-bottom' : 'border-end'}`}
				style={{ minHeight: 0, ...splitMainStyle }}
			>
				{cropperContent}
			</Col>
			<Col xs={12} lg={6} className="px-4 py-3 bay-image-crop-split-side d-flex flex-column" style={{ minHeight: 0 }}>
				{splitContent || (
					<div className="text-muted">{t('Split content area')}</div>
				)}
			</Col>
		</Row>
	);
};

export default SplitLayout;
